/**
 * UrbanPaw — Product Card Variant Dropdown + Quantity Stepper
 * - Handles inline size/weight dropdown selection on collection grid cards.
 * - Updates: selected variant ID, displayed price (qty × unit price), compare price, sold-out state.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function bindCard(card) {
    if (card._upawDropdownBound) return;
    card._upawDropdownBound = true;

    var variantInput = card.querySelector('input.product-variant-id');
    var select       = card.querySelector('.upaw-variant-select:not(.upaw-variant-select--disabled)');
    var qtyDisplay   = card.querySelector('.upaw-qty-display');
    var qtyHidden    = card.querySelector('.upaw-qty-hidden');
    var qtyMinus     = card.querySelector('.upaw-qty-minus');
    var qtyPlus      = card.querySelector('.upaw-qty-plus');

    // ── Unit price tracking ───────────────────────────────────────
    // We store unit price in cents so qty × unitPrice gives the total.
    var unitPrice        = 0;
    var compareUnitPrice = 0;

    function readInitialUnitPrice() {
      // Prefer the dropdown's data attributes (most accurate, avoids currency-symbol parsing)
      if (select) {
        var sel = select.options[select.selectedIndex];
        if (sel && sel.dataset.price) {
          unitPrice        = parseInt(sel.dataset.price, 10)        || 0;
          compareUnitPrice = parseInt(sel.dataset.comparePrice, 10) || 0;
          return;
        }
      }
      // Fallback: parse the rendered price element text for single-variant products
      var priceContainer = card.querySelector('.price');
      if (!priceContainer) return;
      var saleEl    = priceContainer.querySelector('.price-item--sale');
      var regularEl = priceContainer.querySelector('.price-item--regular');
      var primaryEl = saleEl || regularEl;
      if (primaryEl) {
        var raw = primaryEl.textContent.replace(/[^0-9.]/g, '');
        unitPrice = Math.round(parseFloat(raw) * 100) || 0;
      }
      if (saleEl && regularEl) {
        var rawCmp = regularEl.textContent.replace(/[^0-9.]/g, '');
        compareUnitPrice = Math.round(parseFloat(rawCmp) * 100) || 0;
      }
    }

    readInitialUnitPrice();

    // ── Price renderer ────────────────────────────────────────────
    function renderPrice(qty) {
      if (!unitPrice) return;
      var priceContainer = card.querySelector('.price');
      if (!priceContainer) return;

      var totalPrice   = unitPrice * qty;
      var totalCompare = compareUnitPrice * qty;
      var regularEl    = priceContainer.querySelector('.price-item--regular');
      var saleEl       = priceContainer.querySelector('.price-item--sale');

      if (compareUnitPrice && compareUnitPrice > unitPrice) {
        if (saleEl)    saleEl.textContent    = formatMoney(totalPrice);
        if (regularEl) regularEl.textContent = formatMoney(totalCompare);
        priceContainer.classList.add('price--on-sale');
      } else {
        // No compare price: the sale element is hidden once price--on-sale is
        // removed, so the visible price must live in the regular element.
        var displayEl = regularEl || saleEl;
        if (displayEl) displayEl.textContent = formatMoney(totalPrice);
        if (regularEl && saleEl) saleEl.textContent = '';
        priceContainer.classList.remove('price--on-sale');
      }
    }

    // ── Variant dropdown ──────────────────────────────────────────
    if (select) {
      select.addEventListener('change', function () {
        var selected = select.options[select.selectedIndex];
        if (!selected) return;

        var variantId    = selected.value;
        var price        = parseInt(selected.dataset.price, 10)        || 0;
        var comparePrice = parseInt(selected.dataset.comparePrice, 10) || 0;
        var available    = selected.dataset.available !== 'false';

        // Update unit prices for qty calculations
        unitPrice        = price;
        compareUnitPrice = comparePrice;

        // Update hidden variant input
        if (variantInput) {
          variantInput.value    = variantId;
          variantInput.disabled = !available;
        }

        // Update submit button state
        var submitBtn = card.querySelector('button[type="submit"]');
        if (submitBtn) {
          var btnTextSpan = submitBtn.querySelector('span:not(.icon-wrap):not(.loading__spinner)');
          if (available) {
            submitBtn.removeAttribute('disabled');
            submitBtn.classList.remove('disabled');
            if (btnTextSpan) {
              btnTextSpan.textContent = submitBtn.dataset.preorder === 'true' ? 'Preorder' : '+ Cart';
            }
          } else {
            submitBtn.setAttribute('disabled', 'disabled');
            submitBtn.classList.add('disabled');
            if (btnTextSpan) {
              btnTextSpan.textContent = 'Sold out';
            }
          }
        }

        // Re-render price using current qty
        var currentQty = parseInt(qtyDisplay ? qtyDisplay.value : 1, 10) || 1;
        renderPrice(currentQty);
      });
    }

    // ── Quantity stepper ──────────────────────────────────────────
    function syncQty(val) {
      var n = Math.max(1, Math.min(99, parseInt(val, 10) || 1));
      if (qtyDisplay) qtyDisplay.value = n;
      if (qtyHidden)  qtyHidden.value  = n;
      renderPrice(n);
    }

    if (qtyMinus) {
      qtyMinus.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        syncQty((parseInt(qtyDisplay ? qtyDisplay.value : 1, 10) || 1) - 1);
      });
    }

    if (qtyPlus) {
      qtyPlus.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        syncQty((parseInt(qtyDisplay ? qtyDisplay.value : 1, 10) || 1) + 1);
      });
    }

    if (qtyDisplay) {
      qtyDisplay.addEventListener('change', function (e) {
        e.stopPropagation();
        syncQty(qtyDisplay.value);
      });
      qtyDisplay.addEventListener('click', function (e) {
        e.stopPropagation();
      });
    }
  } // end bindCard


  function init() {
    document.querySelectorAll('.product-card-wrapper').forEach(bindCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () { setTimeout(init, 100); });

  var _observer = new MutationObserver(function (mutations) {
    var shouldInit = mutations.some(function (m) {
      return Array.from(m.addedNodes).some(function (n) {
        return n.nodeType === 1 && (
          n.classList.contains('product-card-wrapper') ||
          (n.querySelector && n.querySelector('.product-card-wrapper'))
        );
      });
    });
    if (shouldInit) setTimeout(init, 50);
  });

  var grid = document.querySelector('#product-grid, .collection, main');
  if (grid) _observer.observe(grid, { childList: true, subtree: true });

})();
